import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, ArrowLeft, Wand2, Loader2 } from "lucide-react";
import { QUIZ_STEPS } from "@/lib/config";
import { quizRecommend } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Link, useNavigate } from "react-router-dom";

export default function QuizPage() {
  const { state, setQuizProfile, addToCart, format } = useStore();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    occasion: null, season: null, sillage: null, gender_tilt: null, familiar_scents: [],
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const current = QUIZ_STEPS[step];
  const total = QUIZ_STEPS.length;

  const canAdvance = current.multi
    ? (answers[current.key] || []).length > 0
    : !!answers[current.key];

  const setValue = (val) => {
    if (current.multi) {
      setAnswers((a) => {
        const list = a[current.key] || [];
        return { ...a, [current.key]: list.includes(val) ? list.filter((v) => v !== val) : [...list, val] };
      });
    } else {
      setAnswers((a) => ({ ...a, [current.key]: val }));
    }
  };

  const next = async () => {
    if (step < total - 1) return setStep(step + 1);
    // Submit
    setLoading(true);
    setError(null);
    try {
      const r = await quizRecommend(state.guestId, {
        occasion: answers.occasion,
        season: answers.season,
        sillage: answers.sillage,
        familiar_scents: answers.familiar_scents,
        gender_tilt: answers.gender_tilt,
      });
      setResult(r);
      setQuizProfile(r);
    } catch (e) {
      setError("Our master perfumer is briefly at rest. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setResult(null);
    setStep(0);
    setAnswers({ occasion: null, season: null, sillage: null, gender_tilt: null, familiar_scents: [] });
  };

  return (
    <main data-testid="quiz-page" className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14 lg:py-24">
      {result ? (
        <ResultsView result={result} restart={restart} format={format} addToCart={addToCart} />
      ) : (
        <div>
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059]">
              <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> The Scent Finder
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-3">Find your signature.</h1>
            <p className="mt-3 text-sm sm:text-base text-[#1A1918]/70 max-w-lg mx-auto">Five brief questions. Our Master Perfumer AI matches you to your signature in under 60 seconds.</p>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-[#1A1918]/60 mb-2">
              <div>Step {step + 1} of {total}</div>
              <div>{Math.round(((step + 1) / total) * 100)}%</div>
            </div>
            <Progress value={((step + 1) / total) * 100} className="h-1 bg-[#F1ECE4] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" data-testid="quiz-progress" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`quiz-step-${current.key}`}
            >
              <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/50">{current.title}</div>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2">{current.subtitle}</h2>
              <div className={`mt-8 grid ${current.multi ? "grid-cols-2 md:grid-cols-3 gap-2" : "grid-cols-1 md:grid-cols-2 gap-3"}`}>
                {current.options.map((o) => {
                  const selected = current.multi
                    ? (answers[current.key] || []).includes(o.value)
                    : answers[current.key] === o.value;
                  return (
                    <button
                      key={o.value}
                      data-testid={`quiz-option-${current.key}-${o.value.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                      onClick={() => setValue(o.value)}
                      className={`text-left rounded-2xl border transition-all p-4 ${current.multi ? "py-3" : "p-5"} ${
                        selected
                          ? "bg-[#C5A059]/12 border-[#C5A059] text-[#1A1918]"
                          : "bg-[#F1ECE4] border-black/10 hover:border-[#C5A059]/40 hover:bg-[#FAF8F5]"
                      }`}
                    >
                      <div className={`font-serif ${current.multi ? "text-sm" : "text-lg"}`}>{o.label}</div>
                      {o.desc && !current.multi && <div className="mt-1 text-xs text-[#1A1918]/60">{o.desc}</div>}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex justify-between">
            <Button
              data-testid="quiz-back-button"
              variant="ghost"
              disabled={step === 0 || loading}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="rounded-xl text-xs uppercase tracking-[0.16em] disabled:opacity-30"
            ><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button
              data-testid="quiz-next-button"
              onClick={next}
              disabled={!canAdvance || loading}
              className="bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl px-6 py-5 text-xs uppercase tracking-[0.18em] disabled:opacity-40"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Consulting Perfumer</>
                : step === total - 1 ? <>See My Match <Wand2 className="h-4 w-4 ml-2" /></>
                : <>Continue <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </div>

          {error && <div data-testid="quiz-error" className="mt-6 text-sm text-red-700">{error}</div>}
        </div>
      )}
    </main>
  );
}

function ResultsView({ result, restart, format, addToCart }) {
  const cards = [
    { key: "primary", data: result.primary_match, tag: "Primary Match" },
    { key: "secondary", data: result.secondary_match, tag: "Secondary Match" },
  ];
  return (
    <div data-testid="quiz-results">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059]">
          <Sparkles className="h-3.5 w-3.5" /> Your Olfactory Profile
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">{result.primary_match.match_percentage}% match found.</h1>
        <p className="mt-4 text-[#1A1918]/80 max-w-xl mx-auto italic" data-testid="quiz-scent-profile">{result.scent_profile}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {cards.map((c, idx) => {
          const p = c.data.product;
          if (!p) return null;
          return (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl overflow-hidden border border-[#C5A059]/40 bg-[#F1ECE4]"
              data-testid={`quiz-result-${c.key}`}
            >
              <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                <span className="text-[10px] uppercase tracking-[0.16em] bg-[#C5A059] text-[#1A1918] rounded-full px-2.5 py-1">{c.tag}</span>
                <span className="text-[10px] uppercase tracking-[0.16em] bg-[#1A1918] text-[#FAF8F5] rounded-full px-2.5 py-1">{c.data.match_percentage}% match</span>
              </div>
              <div
                className="aspect-[4/3]"
                style={{ background: `linear-gradient(160deg, ${p.accent_color}30, #F1ECE4)`, backgroundImage: `url(${p.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
              />
              <div className="p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A1918]/60">{p.number}</div>
                <div className="font-serif text-2xl mt-1">{p.name}</div>
                <div className="text-xs text-[#1A1918]/60 mt-1">Inspired by {p.inspired_by} • {p.concentration}</div>
                <p className="mt-3 text-sm text-[#1A1918]/85 leading-relaxed">{c.data.reasoning}</p>
                <div className="mt-5 flex gap-2">
                  <Button
                    data-testid={`quiz-add-to-cart-${c.key}`}
                    className="bg-[#C5A059] text-[#1A1918] hover:bg-[#C5A059]/90 rounded-xl text-xs uppercase tracking-[0.14em]"
                    onClick={() => addToCart(p.id, p.sizes[0].label, 1)}
                  >Add to Cart — {format(p.sizes[0].price_usd)}</Button>
                  <Link to={`/product/${p.slug}`} data-testid={`quiz-view-product-${c.key}`} className="px-4 py-2 rounded-xl border border-black/10 text-xs uppercase tracking-[0.14em] hover:border-[#C5A059]/40 transition-colors flex items-center">View Details</Link>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to="/discovery-builder" data-testid="quiz-build-discovery" className="px-6 py-3 rounded-xl bg-[#1A1918] text-[#FAF8F5] text-xs uppercase tracking-[0.18em] hover:bg-black transition-colors">Build Discovery Set with Top Matches</Link>
        <Button onClick={restart} variant="ghost" data-testid="quiz-retake-button" className="rounded-xl text-xs uppercase tracking-[0.14em]">Retake Quiz</Button>
      </div>
    </div>
  );
}
